// ─────────────────────────────────────────────────────────────────────────────
// Merch, CD's right-rail merch summary. Populated from the Shopify/Square proxy
// (lib fetchMerchSummary). Connected → live "Connected" pill + 30-day/today
// sales + pending; not connected → CD's .live-pill--off "Not connected" + "-"
// values + "Connect →".
//
// ROLE-GATE NOTE: this card (and its $ figures) is a FINANCIAL slot. Per Bible
// 06.5 only the AV owner / Manager / Booking may see money; Tour Manager sees
// per-show but not lifetime, and the read-only Schedule roles (Production,
// Content, Merch crew, Label) never see it. Role gating is OUT OF SCOPE for this
// desktop owner build, when added, gate this whole <section> on the money perm.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import type { MerchSummary } from '../../lib';
import { ArrowRight } from './glyphs';

export interface MerchProps {
  readonly merch: MerchSummary | null;
}

/** Format a money figure with the summary's currency symbol (USD → $). */
function money(currency: string, amount: number): string {
  const symbol = currency === 'USD' || !currency ? '$' : currency + ' ';
  // Whole dollars (CD shows "$64" / "$0"), like the legacy spotlight.
  return symbol + Math.round(amount).toLocaleString('en-US');
}

export function Merch({ merch }: MerchProps): JSX.Element {
  const connected = !!merch && merch.source !== 'none';

  if (!connected) {
    // ── EMPTY / NOT CONNECTED (dashboard-empty.html) ──
    return (
      <section className="card merch" id="merch-summary-card">
        <header className="card__head">
          <h3 className="card__title">Merch</h3>
          <span className="live-pill live-pill--off">Not connected</span>
        </header>
        <div className="card__body" style={{ paddingTop: 0 }}>
          <div className="merch__totals">
            <div className="mstat">
              <p className="mstat__label">Today</p>
              <p className="mstat__value">-</p>
            </div>
            <div className="mstat">
              <p className="mstat__label">30-day</p>
              <p className="mstat__value">-</p>
            </div>
            <div className="mstat">
              <p className="mstat__label">Lifetime</p>
              <p className="mstat__value">-</p>
            </div>
          </div>
          <Link className="merch__pending" to="/settings">
            <span className="merch__pending-label">Pending</span>
            <span className="merch__pending-count">-</span>
            <span className="merch__pending-arrow">
              Connect <ArrowRight />
            </span>
          </Link>
        </div>
      </section>
    );
  }

  // ── POPULATED / CONNECTED ──
  const m = merch!;
  // pending-orders-fix 2026-07-06: pendingOrders is now threaded through
  // MerchSummary (Shopify openCount / Square pendingCount), real count,
  // matching the mobile dashboard, instead of the hardcoded mock "0".
  return (
    <section className="card merch" id="merch-summary-card">
      <header className="card__head">
        <h3 className="card__title">Merch</h3>
        <span className="live-pill">
          <span className="live-pill__dot"></span>Connected
        </span>
      </header>
      <div className="card__body" style={{ paddingTop: 0 }}>
        {/* Today · 30-day · Lifetime. Lifetime is all-time gross and needs the
            read_all_orders scope, '-' until the token is upgraded (the feature
            that justifies that scope). (merch-lifetime-box 2026-07-07, Ryan) */}
        <div className="merch__totals">
          <div className="mstat">
            <p className="mstat__label">Today</p>
            <p className="mstat__value">{money(m.currency, m.salesToday)}</p>
          </div>
          <div className="mstat">
            <p className="mstat__label">30-day</p>
            <p className="mstat__value">{money(m.currency, m.sales30d)}</p>
          </div>
          <div className="mstat">
            <p className="mstat__label">Lifetime</p>
            <p className="mstat__value">{m.salesLifetime != null ? money(m.currency, m.salesLifetime) : '-'}</p>
          </div>
        </div>
        {/* merch-both 2026-07-18 (Ryan): when online + POS come from two
            providers, show the 30-day split under the totals. */}
        {m.onlineSales30d != null && m.posSales30d != null && (
          <p
            className="merch__channel-split"
            style={{ margin: '2px 0 10px', fontFamily: 'var(--ac-font-mono)', fontSize: 11, letterSpacing: '0.04em', color: 'var(--ac-t3)' }}
          >
            Online {money(m.currency, m.onlineSales30d)} · POS {money(m.currency, m.posSales30d)}
          </p>
        )}
        {/* No in-app orders view yet, render as a static stat, not a dead link.
            (Re-enable as a link once an orders/fulfilment screen exists.) */}
        <div className="merch__pending merch__pending--static">
          <span className="merch__pending-label">Pending</span>
          <span className="merch__pending-count">{m.pendingOrders} to fulfill</span>
        </div>
        {/* Best sellers, top 4 products, each a click-through to its storefront
            page, units sold below. Product image + link need read_products.
            (merch-bestsellers 2026-07-07, Ryan) */}
        {m.bestsellers.length > 0 && (
          <div className="merch__best">
            <p className="merch__best-label">Best sellers · 30d</p>
            <div className="merch__best-grid">
              {m.bestsellers.slice(0, 4).map((b, i) => {
                const inner = (
                  <>
                    <span
                      className="merch__best-img"
                      style={b.image_url ? { backgroundImage: `url(${b.image_url})` } : undefined}
                      aria-hidden="true"
                    >
                      {!b.image_url && '♦'}
                    </span>
                    <span className="merch__best-units">{b.units} sold</span>
                  </>
                );
                return b.product_url ? (
                  <a
                    key={i}
                    className="merch__best-card"
                    href={b.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={b.title}
                  >
                    {inner}
                  </a>
                ) : (
                  <span key={i} className="merch__best-card" title={b.title}>
                    {inner}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
